/**
 * Menu merge and normalization logic for AppShell.
 * Pure functions for merging sidebar groups/items/sections with injected menu items.
 * No React hooks or side effects.
 */
import { mergeMenuItems } from '../injection/mergeMenuItems'
import { resolveInjectedIcon } from '../injection/resolveInjectedIcon'
import { slugifySidebarId } from '@open-mercato/shared/modules/navigation/sidebarPreferences'
import type { InjectionMenuItem } from '@open-mercato/shared/modules/widgets/injection'
import type { SectionNavGroup } from '../section-page/types'
import type { AppShellProps } from './appShellTypes'

export type SidebarGroup = AppShellProps['groups'][number]
export type SidebarItem = SidebarGroup['items'][number]

export function resolveGroupKey(group: SidebarGroup): string {
  if (group.id && group.id.length) return group.id
  if (group.defaultName && group.defaultName.length) return slugifySidebarId(group.defaultName)
  return slugifySidebarId(group.name)
}

export function resolveItemKey(item: { id?: string; href: string }): string {
  const candidate = item.id?.trim()
  if (candidate && candidate.length > 0) return candidate
  return item.href
}

export function resolveInjectedMenuLabel(
  item: { id: string; label?: string; labelKey?: string },
  t: (key: string, fallback?: string) => string,
): string {
  if (item.labelKey && item.label) return t(item.labelKey, item.label)
  if (item.labelKey) return t(item.labelKey, item.id)
  if (item.label && item.label.includes('.')) return t(item.label, item.id)
  return item.label ?? item.id
}

export function convertInjectedMenuItemToSidebarItem(item: InjectionMenuItem, title: string): SidebarItem | null {
  if (!item.href) return null
  return {
    id: item.id,
    href: item.href,
    title,
    defaultTitle: title,
    icon: resolveInjectedIcon(item.icon) ?? undefined,
    enabled: true,
    hidden: false,
    pageContext: 'main',
  }
}

export function mergeSidebarItemsWithInjected(
  items: SidebarItem[],
  injectedItems: InjectionMenuItem[],
  t: (key: string, fallback?: string) => string,
): SidebarItem[] {
  if (injectedItems.length === 0) return items

  const builtInById = new Map<string, SidebarItem>()
  for (const item of items) {
    builtInById.set(item.id ?? item.href, item)
  }

  const merged = mergeMenuItems(
    items.map((item) => ({
      id: item.id ?? item.href,
    })),
    injectedItems,
  )

  const result: SidebarItem[] = []
  for (const entry of merged) {
    if (entry.source === 'built-in') {
      const original = builtInById.get(entry.id)
      if (original) result.push(original)
      continue
    }
    const translatedLabel = resolveInjectedMenuLabel(
      { id: entry.id, label: entry.label, labelKey: entry.labelKey },
      t,
    )
    const converted = convertInjectedMenuItemToSidebarItem(
      {
        id: entry.id,
        label: translatedLabel,
        icon: entry.icon,
        href: entry.href,
      },
      translatedLabel,
    )
    if (converted) result.push(converted)
  }

  return result
}

export function mergeSidebarGroupsWithInjected(
  groups: SidebarGroup[],
  injectedItems: InjectionMenuItem[],
  t: (key: string, fallback?: string) => string,
): SidebarGroup[] {
  if (injectedItems.length === 0) return groups

  const injectedByGroup = new Map<string, InjectionMenuItem[]>()
  const ungrouped: InjectionMenuItem[] = []

  for (const item of injectedItems) {
    if (item.groupId && item.groupId.trim().length > 0) {
      const groupItems = injectedByGroup.get(item.groupId) ?? []
      groupItems.push(item)
      injectedByGroup.set(item.groupId, groupItems)
      continue
    }
    ungrouped.push(item)
  }

  const nextGroups = groups.map((group, index) => {
    const groupId = group.id || resolveGroupKey(group)
    const groupInjected = [
      ...(injectedByGroup.get(groupId) ?? []),
      ...(index === 0 ? ungrouped : []),
    ]
    return {
      ...group,
      items: mergeSidebarItemsWithInjected(group.items, groupInjected, t),
    }
  })

  const existingIds = new Set(nextGroups.map((group) => group.id || resolveGroupKey(group)))
  for (const [groupId, items] of injectedByGroup.entries()) {
    if (existingIds.has(groupId)) continue
    const first = items[0]
    const label = first.groupLabelKey
      ? t(first.groupLabelKey, first.groupLabel ?? groupId)
      : (first.groupLabel ?? groupId)
    const groupItems = mergeSidebarItemsWithInjected([], items, t)
    if (groupItems.length === 0) continue
    nextGroups.push({
      id: groupId,
      name: label,
      defaultName: label,
      items: groupItems,
    })
  }

  return nextGroups
}

export function mergeSectionGroupsWithInjected(
  sections: SectionNavGroup[],
  injectedItems: InjectionMenuItem[],
  t: (key: string, fallback?: string) => string,
): SectionNavGroup[] {
  if (injectedItems.length === 0) return sections
  const byGroup = new Map<string, InjectionMenuItem[]>()
  for (const item of injectedItems) {
    const groupId = item.groupId && item.groupId.trim().length > 0 ? item.groupId : 'injected'
    const bucket = byGroup.get(groupId) ?? []
    bucket.push(item)
    byGroup.set(groupId, bucket)
  }

  const nextSections = sections.map((section) => {
    const sectionItems = byGroup.get(section.id) ?? []
    if (sectionItems.length === 0) return section
    const mergedItems = mergeMenuItems(
      section.items.map((item) => ({ id: item.id, item })),
      sectionItems,
    ).flatMap((item) => {
      if (item.source === 'built-in') {
        const original = section.items.find((entry) => entry.id === item.id)
        return original ? [original] : []
      }
      if (!item.href) return []
      const label = resolveInjectedMenuLabel(item, t)
      return [{
        id: item.id,
        label,
        href: item.href,
      }]
    })
    return {
      ...section,
      items: mergedItems,
    }
  })

  for (const [sectionId, sectionItems] of byGroup.entries()) {
    const exists = nextSections.some((section) => section.id === sectionId)
    if (exists) continue
    const first = sectionItems[0]
    const label = first.groupLabelKey
      ? t(first.groupLabelKey, first.groupLabel ?? sectionId)
      : (first.groupLabel ?? sectionId)
    const items = sectionItems.flatMap((item) => {
      if (!item.href) return []
      const itemLabel = resolveInjectedMenuLabel(item, t)
      return [{ id: item.id, label: itemLabel, href: item.href }]
    })
    if (items.length === 0) continue
    nextSections.push({ id: sectionId, label, items })
  }

  return nextSections
}
